import React, { useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { format } from "date-fns";
import { numericalString } from "../../../types";

export default function AttackRemainingTable({
  carNumber,
}: {
  carNumber?: string;
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const nAttackActivations = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data?.NAttackActivations ?? 0,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const attackModeStates = useSelector(
    (state: RootState) => state.attackMode.attackModeStates,
    isEqual,
  );

  const driver = carNumber ? driverInfo?.[carNumber] : undefined;
  const driverColour = driver?.driverHighlightColourAlt;
  const driverFontColour = driver?.driverHighlightColourFontColourAlt;

  const attackModeState = carNumber
    ? attackModeStates?.[carNumber as numericalString]
    : undefined;

  const headers = useMemo(
    () =>
      Array.from({ length: nAttackActivations }).map((_, i) => ({
        key: `atk-${i}`,
        label: `Attack ${i + 1}`,
      })),
    [nAttackActivations],
  );

  const lastActivation = attackModeState?.attackActivations.at(-1);
  const isAttackActive = !!lastActivation && lastActivation.endTime === null;

  const attacksRemainingLive = attackModeState?.attacksRemainingLive ?? [];
  const completedCount = nAttackActivations - attacksRemainingLive.length;

  const attackColumns = headers.map((_, i) => {
    if (i < completedCount) {
      return { remaining: 0, bgColour: "#808080", fontColour: "black" };
    }
    const liveIndex = i - completedCount;
    let remaining = attacksRemainingLive[liveIndex] ?? 0;
    let bgColour = "white";
    let fontColour = "black";
    if (liveIndex === 0 && isAttackActive && lastActivation) {
      const elapsed = (Date.now() - lastActivation.startTime) / 1000;
      remaining = Math.max(remaining - elapsed, 0);
      bgColour = remaining > 0 ? driverColour || "#44bdffff" : "#808080";
      fontColour = remaining > 0 ? driverFontColour || "black" : "black";
    }
    return { remaining, bgColour, fontColour };
  });

  return (
    <div className={styles.container}>
      <table>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header.key}>{header.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {headers.map((header, i) => (
              <td
                key={header.key}
                style={{
                  backgroundColor: attackColumns[i].bgColour,
                  color: attackColumns[i].fontColour,
                  fontWeight: "bold",
                }}
              >
                {format(attackColumns[i].remaining * 1000, "m:ss")}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
